import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearCart, setCurrentScreen, setLoyaltyCode } from '../../store/orderSlice';
import PanarottisIcon from '../../data/PanaImages/PanarottisIcon.png';
import { RootState } from '../../store';

const nexaRustStack = '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
const RECEIPT_WIDTH = 40;

const SuccessScreen: React.FC = () => {
  const dispatch = useDispatch();
  const cart = useSelector((state: RootState) => state.order.cart);
  const orderNumber = useSelector((state: RootState) => state.order.currentOrderNumber);
  const orderTimestamp = useSelector((state: RootState) => state.order.currentOrderTimestamp);
  const isCurrentOrderUnpaid = useSelector((state: RootState) => state.order.isCurrentOrderUnpaid);
  const [shouldPrint, setShouldPrint] = useState(true);

  const dateObject = useMemo(() => {
    if (orderTimestamp) {
      const parsed = new Date(orderTimestamp);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return new Date();
  }, [orderTimestamp]);

  const formattedDate = useMemo(
    () => dateObject.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
    [dateObject]
  );

  const formattedTime = useMemo(
    () => dateObject.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
    [dateObject]
  );

  const orderTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.totalPrice, 0),
    [cart]
  );

  const wrapText = useMemo(() => {
    return (text: string, width: number) => {
      const words = text.split(' ');
      const lines: string[] = [];
      let current = '';
      words.forEach((word) => {
        if ((current + (current ? ' ' : '') + word).length > width) {
          if (current) {
            lines.push(current);
          }
          current = word;
        } else {
          current = current ? `${current} ${word}` : word;
        }
      });
      if (current) {
        lines.push(current);
      }
      return lines;
    };
  }, []);

  const formatLine = useMemo(() => {
    return (left: string, right = '') => {
      if (!right) {
        return wrapText(left, RECEIPT_WIDTH).map((line) => line.padEnd(RECEIPT_WIDTH));
      }

      const amount = right;
      const available = Math.max(0, RECEIPT_WIDTH - amount.length - 1);
      const leftLines = wrapText(left, available);
      if (leftLines.length === 0) {
        return [amount.padStart(RECEIPT_WIDTH)];
      }
      const first = `${leftLines[0].padEnd(available)} ${amount}`;
      const rest = leftLines.slice(1).map((line) => line.padEnd(RECEIPT_WIDTH));
      return [first, ...rest];
    };
  }, [wrapText]);

  const padCenter = useMemo(() => {
    return (text: string) => {
      const clean = text.length > RECEIPT_WIDTH ? text.slice(0, RECEIPT_WIDTH) : text;
      const padding = Math.max(0, Math.floor((RECEIPT_WIDTH - clean.length) / 2));
      return `${' '.repeat(padding)}${clean}`;
    };
  }, []);

  const receiptText = useMemo(() => {
    const lines: string[] = [];
    lines.push(padCenter('NOTIFICATION'));
    lines.push('-'.repeat(RECEIPT_WIDTH));
    lines.push(...formatLine(`Date: ${formattedDate}`));
    lines.push(...formatLine(`Time: ${formattedTime}`));
    lines.push(...formatLine(`Order Number: ${orderNumber ?? 'N/A'}`));
    if (isCurrentOrderUnpaid) {
      lines.push(...formatLine('UNPAID ORDER'));
      lines.push(...formatLine('Take this slip to the counter to pay.'));
    }
    lines.push('');
    lines.push(padCenter('ITEMS'));
    lines.push('-'.repeat(RECEIPT_WIDTH));

    if (cart.length === 0) {
      lines.push(...formatLine('No items'));
    } else {
      cart.forEach((item) => {
        const amount = `R ${item.totalPrice.toFixed(2)}`;
        lines.push(...formatLine(`${item.quantity} x ${item.menuItem.displayName}`, amount));
        item.selectedOptions.forEach((option) => {
          const optionAmount = option.price > 0 ? `R ${option.price.toFixed(2)}` : '';
          lines.push(...formatLine(`- ${option.displayName}`, optionAmount));
        });
        lines.push('');
      });
    }

    lines.push('-'.repeat(RECEIPT_WIDTH));
    lines.push(...formatLine('TOTAL', `R ${orderTotal.toFixed(2)}`));
    lines.push('-'.repeat(RECEIPT_WIDTH));
    lines.push(padCenter('THANK YOU'));

    return lines.join('\n');
  }, [cart, formatLine, formattedDate, formattedTime, isCurrentOrderUnpaid, orderNumber, orderTotal, padCenter]);

  useEffect(() => {
    if (!shouldPrint) {
      return undefined;
    }

    const printTimeout = window.setTimeout(() => {
      window.print();
      setShouldPrint(false);
    }, 300);

    return () => window.clearTimeout(printTimeout);
  }, [shouldPrint]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      dispatch(clearCart());
      dispatch(setLoyaltyCode(null));
      dispatch(setCurrentScreen('welcome'));
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-[#f2f1ec] flex flex-col items-center justify-center relative">
      <pre
        className="hidden print:block"
        style={{ width: '100%', margin: 0, padding: 0, fontFamily: 'Courier New, monospace', fontSize: '20px', lineHeight: '26px' }}
        aria-hidden="true"
      >
        {receiptText}
      </pre>

      <img
        src={PanarottisIcon}
        alt="Panarottis"
        className="w-[136px] h-[102.26px] object-contain print:hidden"
      />
      <h1
        className="mt-8 text-center uppercase text-[#185C53] print:hidden"
        style={{
          fontFamily: nexaRustStack,
          fontSize: '80px',
          lineHeight: '88px',
          fontWeight: 900,
          letterSpacing: '2px',
        }}
      >
        Buon Appetito!
      </h1>
    </div>
  );
};

export default SuccessScreen;

